import type { Spx } from '@wix/thunderbolt-becky-types'
import { getConverter } from '../../utils/convertersRegistry'
import { getStringFromConverter, getKeywordString } from '../../utils/converterHelpers'
import type { StylePropertiesTypes } from '@wix/document-services-types'
import { isSystemFont } from './utils'
import type { FontVariant } from '@wix/thunderbolt-becky-root'

export type FontVariantFunction = Extract<FontVariant['alternates'], { type: 'Function' }>

/** Internal Converters */
const fontWeightConverter = (weight: StylePropertiesTypes.FontWeight): string => {
	const converter = getConverter(weight.type)
	return converter ? getStringFromConverter(converter(weight)) : ''
}

const fontSizeConverter = (size: StylePropertiesTypes.FontSize, spx?: Spx): string => {
	const converter = getConverter(size.type)
	return converter ? getStringFromConverter(converter(size, spx)) : ''
}

const fontStyleConverter = (style: StylePropertiesTypes.FontStyle): string => {
	if (style.value === 'oblique' && style.angle) {
		const angleConverter = getConverter('Angle')
		const angleValue = angleConverter ? getStringFromConverter(angleConverter(style.angle)) : ''
		return `oblique ${angleValue}`
	}
	return style.value
}

const fontLineHeightConverter = (styleProp: StylePropertiesTypes.LineHeight, spx?: Spx): string => {
	const converter = getConverter(styleProp.type)
	return converter ? getStringFromConverter(converter(styleProp, spx)) : ''
}

const fontVariantFunctionConverter = (styleProp: FontVariantFunction) =>
	`${styleProp.function}(${styleProp.values.value.join(', ')})`

const fontVariantConverter = (styleProp: FontVariant) => {
	const ligatures = styleProp.ligatures && getStringFromConverter(getKeywordString(styleProp.ligatures))

	let alternates: string | undefined
	if (styleProp.alternates) {
		if (styleProp.alternates.type === 'Function') {
			alternates = fontVariantFunctionConverter(styleProp.alternates)
		} else {
			alternates = getStringFromConverter(getKeywordString(styleProp.alternates))
		}
	}

	const caps = styleProp.caps && getStringFromConverter(getKeywordString(styleProp.caps))
	const numeric = styleProp.numeric && getStringFromConverter(getKeywordString(styleProp.numeric))
	const eastAsian = styleProp.eastAsian && getStringFromConverter(getKeywordString(styleProp.eastAsian))
	const position = styleProp.position && getStringFromConverter(getKeywordString(styleProp.position))
	const emoji = styleProp.emoji && getStringFromConverter(getKeywordString(styleProp.emoji))

	return {
		ligatures,
		alternates,
		caps,
		numeric,
		eastAsian,
		position,
		emoji,
		[styleProp.type]:
			'var(--{propName}-ligatures) var(--{propName}-alternates) var(--{propName}-caps) var(--{propName}-numeric) var(--{propName}-eastAsian) var(--{propName}-position) var(--{propName}-emoji)',
	}
}

const fontConverter = (styleProp: StylePropertiesTypes.Font, spx?: Spx) => {
	if (isSystemFont(styleProp)) {
		return { font: styleProp.value }
	}

	const { fontStyle, fontVariant, fontWeight, fontSize, lineHeight, fontFamily } = styleProp

	const convertedSize = fontSize && fontSizeConverter(fontSize, spx)
	const convertedStyle = fontStyle && fontStyleConverter(fontStyle)
	const convertedVariant = fontVariant && getStringFromConverter(getKeywordString(fontVariant))
	const convertedWeight = fontWeight && fontWeightConverter(fontWeight)
	const convertedLineHeight = lineHeight && fontLineHeightConverter(lineHeight, spx)

	const listConverter = getConverter('List')
	const convertedFamily = fontFamily && listConverter && getStringFromConverter(listConverter(fontFamily))

	return {
		style: convertedStyle,
		variant: convertedVariant,
		weight: convertedWeight,
		size: convertedSize,
		lineHeight: convertedLineHeight,
		family: convertedFamily,
		[styleProp.type]:
			'var(--{propName}-style) var(--{propName}-variant) var(--{propName}-weight) var(--{propName}-size)/var(--{propName}-lineHeight) var(--{propName}-family)',
	}
}

/** External Map */
export const fontConverterMap = {
	Font: fontConverter,
	FontStyle: fontStyleConverter,
	FontVariant: fontVariantConverter,
}
